// @ts-strict-ignore
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Vector3 } from 'three';
import { Dictionary } from 'typescript-collections';
import { OpenLabelEditorService } from '@project/components/label/label-editor/service/open-label-editor.service';
import { TableProviderGenerator } from '@project/components/label/label-editor/service/table-provider-generator';
import { LabelingTextureData } from '@project/components/label/labeling-texture-data';
import { MetaElementUsageIdentityVO, TextureAreaUsageVO } from '@shared/vo/meta-element.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { IMoveArticlesOperation } from '@shared/undo/operation/i-move-articles-operation';
import { IOperation } from '@shared/undo/operation/i-operation';
import { LabelSequenceOperation } from '@shared/undo/operation/label-sequence-operation';
import { UndoAlternativeReplaceArticleOperation } from '@shared/undo/operation/undo-alternative-replace-article-operation';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { ArticleUsageUtils } from '../../util/article-usage-utils';
import { BaseArticleOperation } from '@shared/undo/operation/base-article-operation';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { ArrayUtils } from '@shared/util/array-utils';
import { StringUtils } from '@shared/util/string-utils';
import { DetachedTextureAreaUsageGroup, DirectPrintingUtils, StoredTextureAreaUsageData } from './direct-printing.utils';
import { UndoReplaceArticleOperation } from '@shared/undo/operation/undo-replace-article-operation';
import { Injectable } from '@angular/core';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { TextureAreaUsageUtils } from '@shared/util/texture-area-usage-utils';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { LoadConnectionTextureProcessor } from '@3d-scene/processor/load-connection-texture.processor';
import type { TextureAreaInfo } from '@project/components/article/manager/base-replace-article.manager';
import { PropertyUtils } from '@article/util/property-utils';

@Injectable({ providedIn: 'root' })
export class DirectPrintingHelper {
  private _storedOperations: IOperation[] = [];

  constructor(
    private _openLabelEditorService: OpenLabelEditorService,
    private _tableProviderGenerator: TableProviderGenerator,
    private _directPrintingUtils: DirectPrintingUtils,
    private containerService: ContainerService,
    private connectionInfoService: ConnectionInfoService,
    private _labelingUtils: LabelingUtils,
    private loadConnectionTextureProcessor: LoadConnectionTextureProcessor,
  ) {}

  public get storedOperations(): IOperation[] {
    return this._storedOperations;
  }

  private storeOperation(operation: IOperation): void {
    if (operation) {
      this._storedOperations.push(operation);
    }
  }

  public clearStoredOperations(): void {
    this._storedOperations = [];
  }

  public recreateDirectPrintingsOnMoveOperation(moveOperation: IMoveArticlesOperation): LabelSequenceOperation {
    let detachedTextureAreas: TextureAreaUsageVO[] = [];
    if (moveOperation instanceof UndoAlternativeReplaceArticleOperation) {
      return null;
    } else if (moveOperation instanceof UndoDetachArticlesOperation) {
      detachedTextureAreas = this.collectTextureAreasByArticleOperations(moveOperation.articleOperations);
    } else if (moveOperation instanceof UndoReplaceArticleOperation) {
      detachedTextureAreas = this.collectTextureAreasByArticleOperations(moveOperation.detachArticleOperations);
    }
    const moveMap: Dictionary<string, Vector3> = moveOperation.getMoveArticlesMap();
    const textureAreas: TextureAreaUsageVO[] = [];
    const containers: ArticleContainer[] =
      moveMap && moveMap.size()
        ? moveMap
            .keys()
            .map((key) => this.containerService.findArticleContainerInGlobal(key))
            .filter((ac) => !!ac)
        : [];
    containers.forEach((ac) =>
      ArrayUtils.mergeArrays(
        textureAreas,
        this._directPrintingUtils.getTextureConnectionsRecursively(ac).map((cn) => cn.metaElementUsage)
      )
    );
    return this.recreateDirectPrintings(
      textureAreas.filter(TextureAreaUsageUtils.mergedTextureAreaUsagesFilter),
      detachedTextureAreas,
      moveOperation.isArticlesOrderChanged()
    );
  }

  public recreateDirectPrintingsOnRotateOperation(rotatedArticleUsageIds: string[] = []): LabelSequenceOperation {
    if (ArrayUtils.isEmpty(rotatedArticleUsageIds)) {
      return null;
    }
    const containers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(rotatedArticleUsageIds);
    const textureAreas: TextureAreaUsageVO[] = [];
    containers.forEach((ac) =>
      ArrayUtils.mergeArrays(
        textureAreas,
        this._directPrintingUtils.getTextureConnectionsRecursively(ac).map((cn) => cn.metaElementUsage)
      )
    );
    return this.recreateDirectPrintings(textureAreas.filter(TextureAreaUsageUtils.mergedTextureAreaUsagesFilter), []);
  }

  public updateDirectPrintingOnConnectAccessoryOperation(acceptingConnectionsToConnect: Connection[]): Observable<void> {
    if (!acceptingConnectionsToConnect && !acceptingConnectionsToConnect.length) {
      return of(null);
    }

    let connections: Connection[] = acceptingConnectionsToConnect;
    acceptingConnectionsToConnect.forEach((cn) => {
      const linkedConnection: Connection = ConnectionInfoUtils.findLinkedMultipurposeConnection(cn);
      if (linkedConnection && connections.indexOf(linkedConnection) === -1) {
        connections.push(linkedConnection);
      }
    });
    connections = connections.filter(connection => connection && connection.metaElementUsage instanceof TextureAreaUsageVO);

    if (!connections || !connections.length) {
      return of(null);
    }

    const connectionsToRefresh =
      connections.flatMap(connection => this._labelingUtils.findAllMergedConnectionsByConnection(connection));

    const cubeIdToLabelDataMap: Dictionary<string, LabelingTextureData> =
      this._openLabelEditorService.storeLabelConnectionsData(connectionsToRefresh);

    this._directPrintingUtils.splitAndRestoreTextureConnections(connectionsToRefresh);

    const undoOperation: IOperation = this._openLabelEditorService.createUndoLabelSequenceOperationForDirectPrinting(
      connectionsToRefresh,
      cubeIdToLabelDataMap
    );
    return this.loadConnectionTextureProcessor.refreshConnectionCubes(connectionsToRefresh).pipe(map(() => this.storeOperation(undoOperation)));
  }

  public updateDirectPrintingOnReplaceAlternativeOperation(
    newArticleIdToReplacedArticleIdMap: Dictionary<string, string>,
    detachOperations: BaseArticleOperation[],
    replacedTextureAreasInfo: Dictionary<string, TextureAreaInfo[]>
  ): LabelSequenceOperation {
    if (!newArticleIdToReplacedArticleIdMap || newArticleIdToReplacedArticleIdMap.isEmpty()) {
      return null;
    }

    const detachedMetaElements = this.collectTextureAreasByArticleOperations(detachOperations);

    const result: { cubeIdToLabelDataMap: Dictionary<string, LabelingTextureData>; connectionsToRefresh: Connection[] } =
      this.updateNewTextureConnectionsCreatedOnReplaceOperation(newArticleIdToReplacedArticleIdMap, replacedTextureAreasInfo);
    const cubeIdToLabelDataMap: Dictionary<string, LabelingTextureData> = result.cubeIdToLabelDataMap;
    const connectionsToRefresh: Connection[] = result.connectionsToRefresh;

    if (!connectionsToRefresh.length || cubeIdToLabelDataMap.isEmpty()) {
      return null;
    }

    this.loadConnectionTextureProcessor.refreshConnectionCubesAsync(connectionsToRefresh);

    return this._openLabelEditorService.createUndoLabelSequenceOperationForDirectPrinting(
      connectionsToRefresh,
      cubeIdToLabelDataMap,
      detachedMetaElements
    );
  }

  private collectTextureAreasByArticleOperations(articleOperations: BaseArticleOperation[]): TextureAreaUsageVO[] {
    return articleOperations
      .flatMap(operation =>
        ArticleUsageUtils.textureAreaUsages(operation.articleUsage)
          .filter(TextureAreaUsageUtils.mergedTextureAreaUsagesFilter));
  }

  private splitAndStoreDirectPrintings(textureAreaUsages: TextureAreaUsageVO[], detachedTextureAreaUsages: TextureAreaUsageVO[] = []): any {
    const storage: StoredTextureAreaUsageData[][] = [];
    const cubeIdToLabelDataMap: Dictionary<string, LabelingTextureData> = new Dictionary<string, LabelingTextureData>();
    const textureConnections: Connection[] = [];

    if (ArrayUtils.isEmpty(textureAreaUsages) && ArrayUtils.isEmpty(detachedTextureAreaUsages)) {
      return { storage, cubeIdToLabelDataMap };
    }

    const updatedTextureAreaUsages: TextureAreaUsageVO[] = this.updateMergedTextureAreasOnDetach(detachedTextureAreaUsages, cubeIdToLabelDataMap);
    ArrayUtils.mergeArrays(textureAreaUsages, updatedTextureAreaUsages);

    const textureConnectionsGroups: Connection[][] = [];
    textureAreaUsages.forEach((textureArea) => {
      const cn: Connection = this.connectionInfoService.findConnectionByIdentity(textureArea.identity);
      if (!textureConnections.includes(cn)) {
        ArrayUtils.mergeArrays(textureConnections, this._labelingUtils.findAllMergedConnectionsByConnection(cn));
      }
      if (textureConnectionsGroups.some((group) => group.indexOf(cn) >= 0)) {
        return;
      } else {
        ArrayUtils.mergeArrays(textureConnectionsGroups, [this._labelingUtils.findAllMergedConnectionsByConnection(cn)]);
      }
    });

    const storedConnectionsData: Dictionary<string, LabelingTextureData> = this._openLabelEditorService.storeLabelConnectionsData(textureConnections);
    storedConnectionsData
      .keys()
      .forEach((k) => !cubeIdToLabelDataMap.containsKey(k) && cubeIdToLabelDataMap.setValue(k, storedConnectionsData.getValue(k)));

    textureConnectionsGroups.forEach((group) => {
      const groupStorage: StoredTextureAreaUsageData[] = [];
      group.forEach((connection) => {
        const labelingData: LabelingTextureData = cubeIdToLabelDataMap.getValue(connection.uid);
        ArrayUtils.mergeArrays(groupStorage, [{ identity: connection.metaElementUsage.identity, labelingData, connection }]);
      });
      if (ArrayUtils.isNotEmpty(groupStorage)) {
        storage.push(groupStorage);
      }
    });

    textureConnections.forEach((cn) => !!cn.articleContainer && cn.articleContainer.restoreConnection(cn.connectionName, true));

    return { storage, cubeIdToLabelDataMap, detachedTextureAreaUsages };
  }

  private recreateDirectPrintings(
    metaElements: TextureAreaUsageVO[],
    detachedMetaElements: TextureAreaUsageVO[] = [],
    isArticlesOrderChanged?: boolean
  ): LabelSequenceOperation {
    const oldData: any = this.splitAndStoreDirectPrintings(metaElements, detachedMetaElements);

    if (!oldData || !oldData.storage.length) {
      return null;
    }

    const connectionsToRefresh: Connection[] = [];
    const cubeIdToLabelDataMap: Dictionary<string, LabelingTextureData> = oldData.cubeIdToLabelDataMap;
    if (isArticlesOrderChanged) {
      this.changeMergedConnectionsOnArticlesOrderChange(oldData.storage);
    }
    oldData.storage.forEach((textureConnectionsToRecreate: StoredTextureAreaUsageData[]) => {
      ArrayUtils.mergeArrays(
        connectionsToRefresh,
        textureConnectionsToRecreate.map((data) => data.connection).filter((cn) => !!cn)
      );
      textureConnectionsToRecreate.forEach((data: StoredTextureAreaUsageData) => {
        if (data.connection) {
          this.updateNearestConnection(data, textureConnectionsToRecreate, connectionsToRefresh, cubeIdToLabelDataMap, true);
          this.updateNearestConnection(data, textureConnectionsToRecreate, connectionsToRefresh, cubeIdToLabelDataMap);
        }
      });
    });

    this.loadConnectionTextureProcessor.refreshConnectionCubesAsync(connectionsToRefresh);
    return this._openLabelEditorService.createUndoLabelSequenceOperationForDirectPrinting(
      connectionsToRefresh,
      cubeIdToLabelDataMap,
      oldData.detachedTextureAreaUsages || []
    );
  }

  private changeMergedConnectionsOnArticlesOrderChange(storage: StoredTextureAreaUsageData[][]): void {
    if (ArrayUtils.isEmpty(storage)) {
      return;
    }
    storage.forEach((textureConnectionsToRecreate) => {
      const connectionsInGroup: Connection[] = textureConnectionsToRecreate.map((data) => data.connection).filter((cn) => !!cn);
      this.splitOrderChangedConnectionsInGroup(connectionsInGroup);
    });
  }

  private splitOrderChangedConnectionsInGroup(connectionsInGroup: Connection[]): void {
    if (ArrayUtils.isEmpty(connectionsInGroup)) {
      return;
    }
    connectionsInGroup.forEach((cn) => cn.articleContainer.updateMatrixWorld(true));
    const sortedConnections: Connection[] = ConnectionInfoUtils.sortConnections(connectionsInGroup, true);
    sortedConnections.forEach((cn) => {
      const cnIndex: number = sortedConnections.findIndex((c) => c === cn);
      const textureUsage: TextureAreaUsageVO = cn.metaElementUsage as TextureAreaUsageVO;
      let newNextMergedUsageId: MetaElementUsageIdentityVO = null;
      let newPreviousMergedUsageId: MetaElementUsageIdentityVO = null;
      if (textureUsage.previousMergedUsageId) {
        const prevIndex: number = sortedConnections.findIndex(
          (c) =>
            c.articleContainer.articleGUID === textureUsage.previousMergedUsageId.articleUsageId &&
            c.metaElementVO.name === textureUsage.previousMergedUsageId.metaElementName
        );
        if (cnIndex - 1 !== prevIndex) {
          if (cnIndex + 1 === prevIndex && this._tableProviderGenerator.isMergeWithNextAvailable(sortedConnections[prevIndex], cn)) {
            newNextMergedUsageId = textureUsage.previousMergedUsageId;
          }
        } else if (!newPreviousMergedUsageId) {
          newPreviousMergedUsageId = textureUsage.previousMergedUsageId;
        }
      }
      if (textureUsage.nextMergedUsageId) {
        const nextIndex: number = sortedConnections.findIndex(
          (c) =>
            c.articleContainer.articleGUID === textureUsage.nextMergedUsageId.articleUsageId &&
            c.metaElementVO.name === textureUsage.nextMergedUsageId.metaElementName
        );
        if (cnIndex + 1 !== nextIndex) {
          if (cnIndex - 1 === nextIndex && this._tableProviderGenerator.isMergeWithNextAvailable(sortedConnections[nextIndex], cn)) {
            newPreviousMergedUsageId = textureUsage.nextMergedUsageId;
          }
        } else if (!newNextMergedUsageId) {
          newNextMergedUsageId = textureUsage.nextMergedUsageId;
        }
      }
      if (!newPreviousMergedUsageId && cnIndex > 0) {
        const prevIndex: number = cnIndex - 1;
        if (this._tableProviderGenerator.isMergeWithNextAvailable(sortedConnections[prevIndex], cn)) {
          newPreviousMergedUsageId = sortedConnections[prevIndex].metaElementUsage.identity;
        }
      }
      if (!newNextMergedUsageId && cnIndex < sortedConnections.length - 1) {
        const nextIndex: number = cnIndex + 1;
        if (this._tableProviderGenerator.isMergeWithNextAvailable(sortedConnections[nextIndex], cn)) {
          newNextMergedUsageId = sortedConnections[nextIndex].metaElementUsage.identity;
        }
      }
      textureUsage.previousMergedUsageId = newPreviousMergedUsageId;
      textureUsage.nextMergedUsageId = newNextMergedUsageId;
    });
  }

  private findStoredConnectionByDataRecursively(
    connectionData: StoredTextureAreaUsageData,
    storedData: StoredTextureAreaUsageData[],
    findPrev?: boolean
  ): Connection {
    const metaUsage: TextureAreaUsageVO = connectionData.connection.metaElementUsage as TextureAreaUsageVO;
    const identityToSearch: MetaElementUsageIdentityVO = findPrev ? metaUsage.previousMergedUsageId : metaUsage.nextMergedUsageId;
    const foundData: StoredTextureAreaUsageData = storedData.find((v) => v.identity.equals(identityToSearch));
    if (!foundData) {
      return null;
    }
    const cn: Connection = foundData.connection;
    if (!cn) {
      return this.findStoredConnectionByDataRecursively(foundData, storedData, findPrev);
    }
    return cn;
  }

  private updateNearestConnection(
    connectionData: StoredTextureAreaUsageData,
    storedData: StoredTextureAreaUsageData[],
    connectionsToRefresh: Connection[],
    cubeIdToLabelDataMap: Dictionary<string, LabelingTextureData>,
    isPrev?: boolean
  ) {
    let nearestConnection: Connection = this.findStoredConnectionByDataRecursively(connectionData, storedData, isPrev);
    const connection: Connection = connectionData.connection;
    const textureAreaUsage: TextureAreaUsageVO = connection.metaElementUsage as TextureAreaUsageVO;
    if (nearestConnection && nearestConnection.metaElementUsage instanceof TextureAreaUsageVO) {
      let isMergeWithNextAvailable: boolean = this._tableProviderGenerator.isMergeWithNextAvailable(nearestConnection, connection);
      if (!isMergeWithNextAvailable) {
        const result: { isMergeWithNextAvailable: boolean; nearestCN: Connection } = this.checkAndUpdateMergeInCaseOfArticlesInsertion(
          connection,
          nearestConnection,
          connectionsToRefresh,
          cubeIdToLabelDataMap,
          isPrev
        );
        isMergeWithNextAvailable = result.isMergeWithNextAvailable;
        nearestConnection = result.nearestCN;
      }
      if (isPrev) {
        textureAreaUsage.previousMergedUsageId = isMergeWithNextAvailable && nearestConnection ? nearestConnection.metaElementUsage.identity : null;
        if (nearestConnection) {
          (nearestConnection.metaElementUsage as TextureAreaUsageVO).nextMergedUsageId = isMergeWithNextAvailable ? connection.metaElementUsage.identity : null;
        }
      } else {
        textureAreaUsage.nextMergedUsageId = isMergeWithNextAvailable && nearestConnection ? nearestConnection.metaElementUsage.identity : null;
        if (nearestConnection) {
          (nearestConnection.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId = isMergeWithNextAvailable ? connection.metaElementUsage.identity : null;
        }
      }
    }
  }

  private checkAndUpdateMergeInCaseOfArticlesInsertion(
    cn: Connection,
    nearestCN: Connection,
    connectionsToRefresh: Connection[],
    cubeIdToLabelDataMap: Dictionary<string, LabelingTextureData>,
    isPrev?: boolean
  ): { isMergeWithNextAvailable: boolean; nearestCN: Connection } {
    let isMergeWithNextAvailable: boolean = false;
    const vDirection: Vector3 = ConnectionInfoUtils.getConnectionVDirection(cn);
    const isVDirectionEqualXDirection: boolean = MathUtils.equalsNumber3D(vDirection, new Vector3(1, 0, 0));
    const isPrevContainerCheck: boolean = (isPrev && isVDirectionEqualXDirection) || (!isPrev && !isVDirectionEqualXDirection);
    let nearestContainer: ArticleContainer = isPrevContainerCheck
      ? this._directPrintingUtils.findPrevArticleContainer(cn.articleContainer)
      : this._directPrintingUtils.findNextArticleContainer(cn.articleContainer);

    let nearestContainers: ArticleContainer[] = nearestContainer
      ? [nearestContainer, ...this.containerService.getConnectedArticleContainersRecursively(nearestContainer)]
      : [];

    if (nearestContainer && nearestContainers.indexOf(nearestCN.articleContainer) === -1) {
      const insertedContainers: ArticleContainer[] = [nearestContainer];
      while (nearestContainer && nearestContainers.indexOf(nearestCN.articleContainer) === -1) {
        nearestContainer = isPrevContainerCheck
          ? this._directPrintingUtils.findPrevArticleContainer(nearestContainer)
          : this._directPrintingUtils.findNextArticleContainer(nearestContainer);
        nearestContainers = nearestContainer
          ? [nearestContainer, ...this.containerService.getConnectedArticleContainersRecursively(nearestContainer)]
          : [];
        if (nearestContainers.length && nearestContainers.indexOf(nearestCN.articleContainer) === -1) {
          insertedContainers.push(nearestContainer);
        }
      }

      const connectionsToMerge: Connection[] = [];
      let connectionToCheckMerge: Connection = cn;

      const storeConnectionToMergeFromInsertedArticle = (mergeConnection: Connection) => {
        connectionsToMerge.push(mergeConnection);
        connectionToCheckMerge = mergeConnection;
        if (mergeConnection) {
          ArrayUtils.mergeArrays(connectionsToRefresh, [mergeConnection]);
          if (!cubeIdToLabelDataMap.containsKey(mergeConnection.uid)) {
            const storedConnectionData: Dictionary<string, LabelingTextureData> = this._openLabelEditorService.storeLabelConnectionsData([
              mergeConnection,
            ]);
            storedConnectionData.keys().forEach((key) => cubeIdToLabelDataMap.setValue(key, storedConnectionData.getValue(key)));
          }
        }
      };

      insertedContainers.forEach((insertedContainer: ArticleContainer) => {
        let mergeConnection: Connection = null;
        if (
          connectionToCheckMerge &&
          connectionToCheckMerge !== cn &&
          ((isPrev && (connectionToCheckMerge.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId) ||
            (!isPrev && (connectionToCheckMerge.metaElementUsage as TextureAreaUsageVO).nextMergedUsageId))
        ) {
          const next: MetaElementUsageIdentityVO = isPrev
            ? (connectionToCheckMerge.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId
            : (connectionToCheckMerge.metaElementUsage as TextureAreaUsageVO).nextMergedUsageId;
          mergeConnection = this.connectionInfoService.findConnectionByIdentity(next);
          storeConnectionToMergeFromInsertedArticle(mergeConnection);
        } else {
          let textureAreas: Connection[] = this._directPrintingUtils.getTextureConnectionsRecursively(insertedContainer);
          textureAreas = textureAreas.filter((ta) => connectionsToMerge.indexOf(ta) === -1);
          mergeConnection = textureAreas.find((t) => this._tableProviderGenerator.isMergeWithNextAvailable(t, connectionToCheckMerge));
          storeConnectionToMergeFromInsertedArticle(mergeConnection);
          if (mergeConnection) {
            const recursivelyMergeTexturesOnArticle = (textureAreas: Connection[], currentCn: Connection) => {
              textureAreas = textureAreas.filter((ta) => ta !== currentCn);
              if (ArrayUtils.isNotEmpty(textureAreas)) {
                currentCn = textureAreas.find((t) => this._tableProviderGenerator.isMergeWithNextAvailable(t, currentCn));
                if (currentCn) {
                  storeConnectionToMergeFromInsertedArticle(currentCn);
                  recursivelyMergeTexturesOnArticle(textureAreas, currentCn);
                }
              }
            };
            recursivelyMergeTexturesOnArticle(textureAreas, mergeConnection);
          }
        }
      });

      const originalNearestCN: Connection = nearestCN;
      connectionsToMerge.forEach((connectionToMerge, index) => {
        const connectionToMergeNext: Connection = connectionsToMerge[index + 1];

        if (index === 0) {
          isMergeWithNextAvailable = !!connectionToMerge;
          nearestCN = connectionToMerge;
        }

        if (!connectionToMergeNext || !connectionToMerge) {
          return;
        }

        if (index >= 0 && index < connectionsToMerge.length) {
          if (isPrev) {
            (connectionToMerge.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId = connectionToMergeNext.metaElementUsage.identity;
            (connectionToMergeNext.metaElementUsage as TextureAreaUsageVO).nextMergedUsageId = connectionToMerge.metaElementUsage.identity;
          } else {
            (connectionToMerge.metaElementUsage as TextureAreaUsageVO).nextMergedUsageId = connectionToMergeNext.metaElementUsage.identity;
            (connectionToMergeNext.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId = connectionToMerge.metaElementUsage.identity;
          }
        }

        if (index === connectionsToMerge.length - 1) {
          if (isPrev) {
            (originalNearestCN.metaElementUsage as TextureAreaUsageVO).nextMergedUsageId = connectionToMerge
              ? (connectionToMerge.metaElementUsage as TextureAreaUsageVO).identity
              : null;
            if (connectionToMerge) {
              (connectionToMerge.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId = originalNearestCN
                ? originalNearestCN.metaElementUsage.identity
                : null;
            }
          } else {
            (originalNearestCN.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId = connectionToMerge
              ? (connectionToMerge.metaElementUsage as TextureAreaUsageVO).identity
              : null;
            if (connectionToMerge) {
              (connectionToMerge.metaElementUsage as TextureAreaUsageVO).nextMergedUsageId = originalNearestCN
                ? originalNearestCN.metaElementUsage.identity
                : null;
            }
          }
        }
      });
    }
    return { isMergeWithNextAvailable, nearestCN };
  }

  private updateNewTextureConnectionsCreatedOnReplaceOperation(
    newArticleIdToReplacedArticleIdMap: Dictionary<string, string>,
    replacedTextureAreasInfo: Dictionary<string, TextureAreaInfo[]>
  ): { cubeIdToLabelDataMap: Dictionary<string, LabelingTextureData>; connectionsToRefresh: Connection[] } {
    if (!newArticleIdToReplacedArticleIdMap || newArticleIdToReplacedArticleIdMap.isEmpty()) {
      return null;
    }
    const cubeIdToLabelDataMap: Dictionary<string, LabelingTextureData> = new Dictionary<string, LabelingTextureData>();
    const connectionsToRefresh: Connection[] = [];
    newArticleIdToReplacedArticleIdMap.keys().forEach((key) => {
      const replacedArticleGuid: string = newArticleIdToReplacedArticleIdMap.getValue(key);
      const replacedArticleTextureInfos: TextureAreaInfo[] = replacedTextureAreasInfo.getValue(replacedArticleGuid);

      const newContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(key);

      const prevContainer: ArticleContainer = this._directPrintingUtils.findPrevArticleContainer(
        newContainer,
        newArticleIdToReplacedArticleIdMap.values()
      );
      const prevMergedTextureAreas: Connection[] = prevContainer
        ? this.findReplacedTextureConnectionsFromArticle(prevContainer, replacedArticleGuid, newArticleIdToReplacedArticleIdMap, true)
        : [];

      const nextContainer: ArticleContainer = this._directPrintingUtils.findNextArticleContainer(
        newContainer,
        newArticleIdToReplacedArticleIdMap.values()
      );
      const nextMergedTextureAreas: Connection[] = nextContainer
        ? this.findReplacedTextureConnectionsFromArticle(nextContainer, replacedArticleGuid, newArticleIdToReplacedArticleIdMap)
        : [];

      const connectionsToStore: Connection[] = [];
      [...prevMergedTextureAreas, ...nextMergedTextureAreas].forEach((cn) => {
        ArrayUtils.mergeArrays(connectionsToStore, this._labelingUtils.findAllMergedConnectionsByConnection(cn));
      });

      const replacedCubeIdToLabelDataMap: Dictionary<string, LabelingTextureData> =
        this._openLabelEditorService.storeLabelConnectionsData(connectionsToStore);
      replacedCubeIdToLabelDataMap
        .keys()
        .forEach((key) => !cubeIdToLabelDataMap.containsKey(key) && cubeIdToLabelDataMap.setValue(key, replacedCubeIdToLabelDataMap.getValue(key)));
      connectionsToStore.forEach((cn) => cn.articleContainer.restoreConnection(cn.connectionName, true));

      const newTextureAreaConnections: Connection[] = newContainer.connections.filter((cn) => cn.metaElementUsage instanceof TextureAreaUsageVO);

      const newCubeIdToLabelDataMap: Dictionary<string, LabelingTextureData> =
        this._openLabelEditorService.storeLabelConnectionsData(newTextureAreaConnections);
      newCubeIdToLabelDataMap
        .keys()
        .forEach((key) => !cubeIdToLabelDataMap.containsKey(key) && cubeIdToLabelDataMap.setValue(key, newCubeIdToLabelDataMap.getValue(key)));

      ArrayUtils.mergeArrays(connectionsToRefresh, connectionsToStore);

      this.updateNewTextureConnectionsDuringReplace(
        newTextureAreaConnections,
        replacedArticleTextureInfos,
        connectionsToRefresh,
        prevMergedTextureAreas,
        nextMergedTextureAreas
      );
    });
    return { cubeIdToLabelDataMap, connectionsToRefresh };
  }

  private updateNewTextureConnectionsDuringReplace(
    newTextureAreaConnections: Connection[],
    replacedArticleTextureInfos: TextureAreaInfo[],
    connectionsToRefresh: Connection[],
    prevMergedTextureAreas: Connection[],
    nextMergedTextureAreas: Connection[]
  ): void {
    newTextureAreaConnections.forEach((cn) => {
      const hasDefaultText = PropertyUtils.containsProperty(cn.metaElementVO.behaviorProperties, 'defaultText') &&
        StringUtils.isNotEmpty(PropertyUtils.getStringPropertyValue(cn.metaElementVO.behaviorProperties, 'defaultText'));
      if (!hasDefaultText && ArrayUtils.isNotEmpty(replacedArticleTextureInfos)) {
        const replacedCns: TextureAreaInfo[] = replacedArticleTextureInfos.filter((r) => {
          return this._directPrintingUtils.checkConnectionRectanglesAlignment(r.compositionRectangle, cn.compositionRectangle);
        });
        let replacedCn: TextureAreaInfo;
        if (replacedCns.length) {
          replacedCn = replacedCns.find((r) =>
            this._directPrintingUtils.isConnectionRectanglesIntersected(r.compositionRectangle, cn.compositionRectangle)
          );
          if (!replacedCn) {
            const rect: Rectangle3D = this._directPrintingUtils.getNearestRectangleFromArray(
              replacedCns.map((r) => r.compositionRectangle),
              cn.compositionRectangle
            );
            if (rect) {
              replacedCn = replacedCns.find((r) => MathUtils.equalsRectangle3D(r.compositionRectangle, rect));
            }
          }
          if (replacedCn) {
            (cn.metaElementUsage as TextureAreaUsageVO).textValue = replacedCn.textValue;
            (cn.metaElementUsage as TextureAreaUsageVO).imageUrl = replacedCn.imageUrl;
            if (StringUtils.isNotEmpty(replacedCn.imageHash)) {
              ArticleUsageUtils.setDefinedMetaElementInfoPropertyStringValue(
                cn.articleContainer.articleUsage,
                cn.connectionName,
                DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH,
                replacedCn.imageHash
              );
            }
            if (replacedCn.previousMerged) {
              prevMergedTextureAreas.forEach((prev) => {
                if (this._tableProviderGenerator.isMergeWithNextAvailable(prev, cn)) {
                  (cn.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId = prev.metaElementUsage.identity;
                  (prev.metaElementUsage as TextureAreaUsageVO).nextMergedUsageId = cn.metaElementUsage.identity;
                } else {
                  (cn.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId = null;
                  (prev.metaElementUsage as TextureAreaUsageVO).nextMergedUsageId = null;
                }
                ArrayUtils.mergeArrays(connectionsToRefresh, [cn, prev]);
              });
            }
            if (replacedCn.nextMerged) {
              nextMergedTextureAreas.forEach((next) => {
                if (this._tableProviderGenerator.isMergeWithNextAvailable(cn, next)) {
                  (cn.metaElementUsage as TextureAreaUsageVO).nextMergedUsageId = next.metaElementUsage.identity;
                  (next.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId = cn.metaElementUsage.identity;
                } else {
                  (cn.metaElementUsage as TextureAreaUsageVO).nextMergedUsageId = null;
                  (next.metaElementUsage as TextureAreaUsageVO).previousMergedUsageId = null;
                }
                ArrayUtils.mergeArrays(connectionsToRefresh, [cn, next]);
              });
            }
          }
        }
      }
    });
  }

  private findReplacedTextureConnectionsFromArticle(
    articleContainer: ArticleContainer,
    replacedArticleGuid: string,
    newArticleIdToReplacedArticleIdMap: Dictionary<string, string>,
    isPrev?: boolean
  ): Connection[] {
    if (!articleContainer || ArrayUtils.isEmpty(articleContainer.connections)) {
      return [];
    }
    const replacedTextureAreaConnections: Connection[] = articleContainer.connections.filter(
      (cn) =>
        cn.metaElementUsage instanceof TextureAreaUsageVO &&
        (newArticleIdToReplacedArticleIdMap.containsKey(articleContainer.articleGUID) ||
          (isPrev
            ? cn.metaElementUsage.nextMergedUsageId && cn.metaElementUsage.nextMergedUsageId.articleUsageId === replacedArticleGuid
            : cn.metaElementUsage.previousMergedUsageId && cn.metaElementUsage.previousMergedUsageId.articleUsageId === replacedArticleGuid))
    );

    const forwardConnected: ArticleContainer[] = ArticleUsageUtils.getAcceptingAreaIds(articleContainer.articleUsage)
      .map((guid) => this.containerService.findArticleContainerInGlobal(guid))
      .filter((ac) => !!ac);
    forwardConnected.map((c) =>
      ArrayUtils.mergeArrays(
        replacedTextureAreaConnections,
        this.findReplacedTextureConnectionsFromArticle(c, replacedArticleGuid, newArticleIdToReplacedArticleIdMap, isPrev)
      )
    );

    return replacedTextureAreaConnections;
  }

  private updateMergedTextureAreasOnDetach(
    detachedTextureAreaUsages: TextureAreaUsageVO[],
    originalAreasStorage: Dictionary<string, LabelingTextureData> = new Dictionary<string, LabelingTextureData>()
  ): TextureAreaUsageVO[] {
    const updatedTextureAreas: TextureAreaUsageVO[] = [];
    if (ArrayUtils.isNotEmpty(detachedTextureAreaUsages)) {
      const storeUnchangedConnectionData = (cn: Connection) => {
        if (!originalAreasStorage.containsKey(cn.uid)) {
          const stored: Dictionary<string, LabelingTextureData> = this._openLabelEditorService.storeLabelConnectionsData([cn]);
          if (stored) {
            stored.keys().forEach((k) => originalAreasStorage.setValue(k, stored.getValue(k)));
          }
        }
      };
      const groups: DetachedTextureAreaUsageGroup[] = this._directPrintingUtils.groupDetachedTextureAreaUsages(detachedTextureAreaUsages.concat());
      groups.forEach((group) => {
        const prev: Connection = this.connectionInfoService.findConnectionByIdentity(group.start.previousMergedUsageId);
        if (prev) {
          prev.articleContainer.restoreConnection(prev.connectionName, true);
        }
        const next: Connection = this.connectionInfoService.findConnectionByIdentity(group.end.nextMergedUsageId);
        if (next) {
          next.articleContainer.restoreConnection(next.connectionName, true);
        }
        const isMergeWithNextAvailable: boolean = this._tableProviderGenerator.isMergeWithNextAvailable(prev, next);
        if (prev && prev.metaElementUsage instanceof TextureAreaUsageVO) {
          storeUnchangedConnectionData(prev);
          prev.metaElementUsage.nextMergedUsageId = isMergeWithNextAvailable ? group.end.nextMergedUsageId : null;
          ArrayUtils.mergeArrays(updatedTextureAreas, [prev.metaElementUsage]);
        }
        if (next && next.metaElementUsage instanceof TextureAreaUsageVO) {
          storeUnchangedConnectionData(next);
          next.metaElementUsage.previousMergedUsageId = isMergeWithNextAvailable ? group.start.previousMergedUsageId : null;
          ArrayUtils.mergeArrays(updatedTextureAreas, [next.metaElementUsage]);
        }
      });
    }
    return updatedTextureAreas;
  }
}
